// Copyright (c) StrangeBee 2023

import { useLocalMessage } from '@Helpers/locale';
import { DisplayCustomFields } from '@Organisms/DisplayCustomFields/DisplayCustomFields';
import DisplayTag from '@Organisms/DisplayTag/DisplayTag';
import RouteBuilder from '@Services/RouteBuilder';
import React, { ReactElement, useContext } from 'react';
import { Link } from 'react-router-dom';
import { AlertOutput, CaseCustomField, SeverityValues } from 'thehive-sdk';
import { classList } from '@Helpers/className';
import { ICONS } from '@Constants/iconsApp';
import { PreviewButton } from '@Atoms/Buttons';
import { ScopeContext } from '@Context/ScopeProvider';
import { SeverityTag } from '@Atoms/Info/SeverityTag';

import './AlertListTitle.scss';

interface AlertListTitleProps {
    data: AlertOutput;
    onTagsClick?: (tagValue: string, event: React.MouseEvent<HTMLSpanElement, MouseEvent>) => void;
    onCustomFieldsClick?: (customField: CaseCustomField) => void;
    customFieldsIsOpen?: boolean;
    tagsIsOpen?: boolean;
    onPreviewClick?: () => void;
    path?: string;
    onSeverityClick?: (severity: SeverityValues) => void;
}

export const AlertListTitle = ({
    data,
    customFieldsIsOpen = true,
    tagsIsOpen = true,
    onTagsClick,
    onCustomFieldsClick,
    onPreviewClick,
    path,
    onSeverityClick,
}: AlertListTitleProps): ReactElement => {
    const i = useLocalMessage();

    const { title, _id: alertId, tags = [], customFields = [], severity } = data;
    const customFieldsFiltered = customFields.filter(({ value }) => value);
    const { coloredTags } = useContext(ScopeContext);

    return (
        <div className="alert-list-title">
            <div className="alert-list-title__row">
                <SeverityTag
                    severity={severity}
                    onClick={(): void => {
                        onSeverityClick?.(severity);
                    }}
                />

                <Link
                    to={path || RouteBuilder.compute('alerts.details', { alertId })}
                    className={classList('alert-list-title__link')}
                >
                    {title}
                </Link>
            </div>

            {tagsIsOpen && (
                <div className="alert-list-title__tags">
                    <ICONS.TagsOutlined className="alert-list-title__icon" />
                    {tags.length ? (
                        <DisplayTag.List<string> data={tags} limit={5}>
                            {(tag) => <DisplayTag tag={tag} onClick={onTagsClick} color={coloredTags[tag]} />}
                        </DisplayTag.List>
                    ) : (
                        <span className="case-additional-row__text">{i('general.none')}</span>
                    )}
                </div>
            )}

            {customFieldsIsOpen && (
                <div className="alert-list-title__custom-fields">
                    <ICONS.ControlOutlined className="alert-list-title__icon" />
                    {customFieldsFiltered.length ? (
                        <DisplayCustomFields customFields={customFieldsFiltered} onClick={onCustomFieldsClick} />
                    ) : (
                        <span className="case-additional-row__text">{i('general.none')}</span>
                    )}
                </div>
            )}

            {onPreviewClick && (
                <div className="generic-drawer-preview">
                    <PreviewButton onClick={onPreviewClick} />
                </div>
            )}
        </div>
    );
};
